import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter } from 'recharts';
import { AnalysisResult, Trial } from '../types';

interface PowerTimeChartProps {
  result: AnalysisResult;
  trials: Trial[];
}

export const PowerTimeChart: React.FC<PowerTimeChartProps> = ({ result, trials }) => {
  const { advancedProfiling } = result;
  
  if (!advancedProfiling) return null;

  const trialsWithBoth = trials.filter(t => t.power_W !== undefined && t.sprint_time_s !== undefined);
  
  const data = trialsWithBoth.map(trial => ({
    time: trial.sprint_time_s,
    power: trial.power_W,
    load: trial.load_kgf
  }));

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Power-Time Analysis</h3>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="time" 
              stroke="#9CA3AF"
              label={{ value: 'Sprint Time (s)', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#9CA3AF' } }}
            />
            <YAxis 
              stroke="#9CA3AF"
              label={{ value: 'Power (W)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#9CA3AF' } }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151', 
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
              formatter={(value: number, name: string) => [
                name === 'power' ? `${value} W` : `${value} s`,
                name === 'power' ? 'Power' : 'Time'
              ]}
              labelFormatter={(label) => `Load: ${data.find(d => d.time === label)?.load || 0} kg`}
            />
            <Scatter 
              dataKey="power" 
              fill="#60A5FA" 
              stroke="#60A5FA"
              strokeWidth={2}
              r={6}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-theme-muted">
        <p>Peak Power Timing: <span className="text-theme-accent font-semibold">{advancedProfiling.peakPowerTiming.toFixed(2)} s</span></p>
        <p>Power-Time Slope: <span className="text-theme-accent font-semibold">{advancedProfiling.powerTimeSlope.toFixed(1)} W/s</span></p>
        <p>Metabolic Contribution: <span className="text-theme-accent font-semibold">{advancedProfiling.metabolicContribution.toFixed(1)}%</span></p>
      </div>
    </div>
  );
};